import i18n from 'src/lib/i18n/i18n';
import { PaymentResult } from '../../iap.types';
import { user } from 'src/StateObserver';
import { useEffect, useRef, useState } from 'preact/hooks';
import { PayButton } from '../PayButton';
import { ProductId } from 'src/replicant/iap/iap.ruleset';
import {
  getKomojuHostedFields,
  getKomojuSessionCache,
  loadKomojuHostedFieldsSDK,
} from '../../providers/komoju';
import { AgeConsent } from '../AgeConsent';
import { spawnPaymentWindow } from '../../iap.utils';
import { RakutenPayButton } from '../RakutenPayButton';

/**
 * Komoju API payment methods without the `komoju_` prefix.
 */
type KomojuPaymentMethodsWithoutPrefix =
  | 'credit_card'
  | 'linepay'
  | 'rakutenpay'
  | 'paypay'
  | 'paidy'
  | 'aupay'
  | 'docomo'
  | 'softbank'
  | 'au'
  | 'merpay';

/**
 * Handle komoju payments. Show required fields and forms according to the payment method.
 * @param
 * @returns
 */
export function KomojuHandler({
  productId,
  paymentMethod,
  close,
  onPurchaseStart,
  onAgeConfirm,
}: {
  productId: ProductId;
  paymentMethod: KomojuPaymentMethodsWithoutPrefix;
  close: (result: PaymentResult) => void;
  onPurchaseStart: () => void;
  onAgeConfirm: (accepted: boolean) => void;
}) {
  // Age consent state.
  const [ageConsent, setAgeConsent] = useState(true);

  // Current session id.
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  // State of komoju sdk.
  const [initialized, setInitialized] = useState(false);

  // Whether the form is submitted.
  const [submitted, setSubmitted] = useState(false);

  // On mount.
  useEffect(() => {
    // Set the payment type.
    const fields = getKomojuHostedFields();
    fields.setAttribute('payment-type', paymentMethod);

    // Set the session id when ready.
    Promise.all([
      loadKomojuHostedFieldsSDK(),
      getKomojuSessionCache(productId).get(),
      fields.initPromise,
    ]).then(([, sessionId]) => {
      fields?.setAttribute('style', ``);
      setSessionId(sessionId);
      setInitialized(true);
    });

    // On unmount hide the fields.
    return () => {
      fields.setAttribute('style', `display: none;`);
    };
  }, []);

  // Handle the payment.
  const handleKomojuPayment = async (e: Event) => {
    // Prevent multiple submissions.
    if (submitted) return;
    setSubmitted(true);

    e.preventDefault();

    // Komoju appends a hidden <input> element to our form containing the token
    const result = await getKomojuHostedFields().submit();

    // Field might not be ready, allow the user to try again.
    if (!result) {
      setSubmitted(false);
      return;
    }

    onPurchaseStart();

    // Credit card payments don't need to be handled in a separate window.
    if (paymentMethod !== 'credit_card') {
      // Create window here, so it's not blocked by the browser.
      spawnPaymentWindow();
    }

    // Complete the payment.
    close({
      success: true,
      token: result.id,
      provider: 'komoju',
      // We don't have a purchaseID yet
      purchaseID: 'unknown',
      internalTransactionId: sessionId ?? 'unknown',
    });
  };

  // Return the Komoju fields container. And add extra information/requirements.
  return (
    <>
      {paymentMethod === 'rakutenpay' ? (
        <RakutenPayButton
          disabled={!ageConsent || !initialized || submitted}
          loading={!initialized}
          onClick={handleKomojuPayment}
        />
      ) : (
        <PayButton
          disabled={!ageConsent || !initialized || submitted}
          loading={!initialized}
          onClick={handleKomojuPayment}
        />
      )}

      {
        // Show age consent checkbox if the user has not accepted it.
        !user().ageConsentJPAccepted && (
          <AgeConsent
            onChange={(consent) => {
              onAgeConfirm(consent);
              setAgeConsent(consent);
            }}
          />
        )
      }
    </>
  );
}
